import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { EMPTY, Subject } from 'rxjs';
import { catchError, concatMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { IClassOption } from '../../models/kps.model';
import { ISelectOption } from '../../models/location.model';

import { AuthService, OtpVerify } from '../../services/auth.service';
import { KpsService } from '../../services/kps.service';
import { TranslocoService } from '@ngneat/transloco';
import { DocumentService } from "../../services/document.service";
import moment from 'moment';
import { IRequestLogin } from "@shared/models/auth.model";
import { ReCaptchaV3Service } from "ng-recaptcha";

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit, OnDestroy {
  @ViewChild('signupErrorTpl', {static: true}) signupErrorTpl!: TemplateRef<{}>;
  @ViewChild('loginErrorTpl', {static: true}) loginErrorTpl!: TemplateRef<{}>;

  signupForm!: FormGroup;
  signupError = '';
  // pwReg = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,12}$/;
  emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z]+[a-z0-9.-]+\.[a-z]{2,4}$/;
  phoneReg = /(0[3|5|7|8|9])+([0-9]{8})\b/;
  pwValidatorRes = {letter: false, number: false, special: false, length: false};
  passwordVisible = false;
  classList: ISelectOption[] = [];
  destroy$ = new Subject<void>();
  isSignupEmail = false;
  isSignupPhone = false;
  loginError = '';
  loading = false;
  logo = '';
  showNotiPopup = false;

  mode: 'signup' | 'otp' = 'signup';
  otpObj?: OtpVerify;
  otpCode = '';
  otpExpiredTime = 0;
  enableVerifyBtn = false;
  isVerifying = false;
  signupFormByPhone!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private notificationService: NzNotificationService,
    private kpsService: KpsService,
    private translocoService: TranslocoService,
    private documentService: DocumentService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    this.initSignupForm();
    this.onPwChanged();
    this.getClassList();
    this.trackSocialLogin();
    this.getLogo();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackSocialLogin(): void {
    this.authService.trackSocialUser()
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
        },
        error: (err) => {
          this.trackSocialLogin();
          this.authService.signOutSocial();
          this.handleLoginFailed(err);
        }
      });
  }

  initSignupForm(): void {
    this.signupForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.pattern(this.emailReg)]],
      password: ['', [Validators.required, Validators.minLength(3)]],
      class: ['', [Validators.required]]
    });

    this.signupFormByPhone = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(50)]],
      phone: ['', [Validators.required, Validators.pattern(this.phoneReg)]],
      password: ['', [Validators.required, Validators.minLength(3)]],
      class: ['', [Validators.required]]
    });

  }

  signup(): void {
    if (this.signupForm.valid || this.signupFormByPhone.valid) {
      this.loading = true;
      this.recaptchaV3Service.execute('signup').pipe(
        catchError(() => {
          this.notificationService.error(
            '',
            this.translocoService.translate('common.failed'),
          );
          return EMPTY;
        }),
        concatMap((token) => {
          this.authService.setValueRecapcha(token);
          return this.authService
            .signup(this.isSignupEmail ? this.signupForm.value : this.signupFormByPhone.value)
            .pipe(
              catchError((err) => {
                this.loading = false;
                this.signupError = this.translocoService.translate('signup.signupFailed');
                if (err.error.error.messageKey === 'user_email_duplicate') {
                  this.signupError = this.translocoService.translate('signup.existEmail');
                }
                if (err.error.error.messageKey === 'user_phone_duplicate') {
                  this.signupError = this.translocoService.translate('signup.existPhone');
                }
                this.notificationService.template(
                  this.signupErrorTpl,
                  {
                    nzCloseIcon: '',
                    nzStyle: {backgroundColor: 'transparent', padding: 0}
                  }
                );
                return EMPTY;
              }),
            )
        })
      ).subscribe(() => {
        this.loading = false;
        this.notificationService.success(
          this.translocoService.translate('signup.signupSuccessTitle'),
          this.translocoService.translate('signup.signupSuccessMsg')
        );
        this.router.navigateByUrl('login');
      })
    }
  }

  getOtpInfo(email: string) {
    this.authService.getOtpInfo({email})
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.otpExpiredTime = Math.floor(moment.duration(moment(res.expriedAt).diff(moment(res.createdAt))).asMinutes());
      })
  }

  onOtpInputChanged(code: string) {
    if (code.length === 6) {
      this.enableVerifyBtn = true;
      this.otpCode = code;
    } else {
      this.enableVerifyBtn = false;
      this.otpCode = '';
    }
  }

  verifyOtp() {
    if (!this.enableVerifyBtn) {
      return;
    }
    this.isVerifying = true;
    this.otpObj!.code = this.otpCode;
    this.authService.verifyOtp(this.otpObj!)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.isVerifying = false;
          const req = {
            email: this.signupForm.get('email')?.value,
            password: this.signupForm.get('password')?.value
          }
          this.login(req);
        },
        error: (err) => {
          this.isVerifying = false;
          if (err.messageKey === 'account_activate_code_not_match') {
            this.notificationService.error(
              '',
              this.translocoService.translate('signup.wrongCode')
            );
            return;
          }
          this.notificationService.error(
            '',
            this.translocoService.translate('common.error_occured_please_try_again')
          );
        }
      });
  }

  resendOtp() {
    this.authService.resendOtp({email: this.signupForm.get('email')?.value})
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res) => {
          this.notificationService.success(
            '',
            this.translocoService.translate('signup.resendSuccess')
          );
          this.otpExpiredTime = Math.floor(moment.duration(moment(res.expriedAt).diff(moment(res.createdAt))).asMinutes());
        },
        error: () => {
          this.notificationService.error(
            '',
            this.translocoService.translate('common.error_occured_please_try_again')
          );
        }
      });
  }

  login(req: IRequestLogin) {
    this.authService
      .login(req)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
        },
        error: (err) => {
          this.handleLoginFailed(err.status);
        }
      });
  }

  getClassList(): void {
    this.kpsService
      .getKpsClass()
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.classList = res.items.map((ele: IClassOption): ISelectOption => ({
          key: ele.code,
          label: ele.name
        }))
      });
  }

  onPwChanged() {
    this.signupForm.get('password')?.valueChanges.subscribe((value) => {
      // this.pwValidatorRes.letter = /[a-zA-Z]/.test(value);
      // this.pwValidatorRes.number = /[0-9]/.test(value);
      // this.pwValidatorRes.special = /[!@#$%^&*]/.test(value);
      // this.pwValidatorRes.length = value.length > 7 && value.length < 13;
      this.pwValidatorRes.length = value.length > 2;
    });
    this.signupFormByPhone.get('password')?.valueChanges.subscribe((value) => {
      this.pwValidatorRes.length = value.length > 2;
    });
  }

  signInWithFb(): void {
    this.authService.signInWithFb();
  }

  signInWithGg(): void {
    this.authService.signInWithGg();
  }

  signInWithEmail(): void {
    this.isSignupEmail = true;
    this.isSignupPhone = false;
    this.showNotiPopup = true;
    this.signupForm.reset({
      firstName: '',
      email: '',
      password: '',
      class: ''
    });
  }

  signInWithPhone(): void {
    this.isSignupEmail = false;
    this.isSignupPhone = true;
    this.showNotiPopup = true;
    this.signupFormByPhone.reset({
      firstName: '',
      phone: '',
      password: '',
      class: ''
    });
  }

  handleLoginFailed(err: any): void {
    this.loginError = this.translocoService.translate('signup.loginFailed');
    if (err.status === 400) {
      this.loginError = this.translocoService.translate('signup.existEmail');
      if (err.error.error.messageKey === 'social_auth_login_require_email') {
        this.loginError = this.translocoService.translate('login.socialRequireEmail');
      }
    }
    this.notificationService.template(
      this.loginErrorTpl,
      {
        nzCloseIcon: '',
        nzStyle: {backgroundColor: 'transparent', padding: 0}
      }
    );
  }

  getLogo() {
    this.logo = this.documentService.getLogoPage();
    if (!this.logo) {
      this.documentService.getLogo().pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.logo = res.logo;
          this.documentService.setLogoPage(this.logo);
        }
      })
    }
  }
}
